import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Delete, Gift, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatMoney, rovingKeyDown } from './ItemConfiguration'
import type { PosPaymentInput, PosVoucher } from './types'
import { CashRegisterNotice, useCashRegister } from './CashRegisterContext'
import { CashMark, denominationName } from './cashDenominations'
import { VoucherEntry } from './VoucherEntry'
import { AMOUNT, FLAT, MAIN, PICKED, QUIET, SEGMENT, SEGMENT_ON, SEGMENTS } from './posStyles'

const PAD_KEY = 'pos-cash-pad'
const SIDE = cn(QUIET, 'h-[68px] bg-card px-3 pos-wide:h-[60px] text-base shadow-[0_0_0_1px_var(--pos-hair)] hover:bg-card/70 hover:shadow-[0_0_0_1px_var(--pos-hair)] sm:px-4')
// Laid out like a till: notes above, coins below. 1 and 2 cent stay on the number pad: they are rarely handed over.
const CASH_NOTES = [20000, 10000, 5000, 2000, 1000, 500], CASH_COINS = [200, 100, 50, 20, 10, 5]
/** Parse entered money without binary floating-point calculations. */
export function parseTenderedCents(text: string): number | null {
  if (!/^\d{1,7}(?:[.,]\d{1,2})?$/.test(text.trim())) return null
  const [whole, fraction = ''] = text.trim().replace(',', '.').split('.')
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return Number.isSafeInteger(cents) && cents <= 100_000_000 ? cents : null
}

export function PaymentForm({ totalCents: amountDue, disabled = false, busy = false, onConfirm, onPrepareCard: prepareCard, initialMethod = 'cash', restaurantId, noTip = false }: {
  totalCents: number; disabled?: boolean; busy?: boolean; onConfirm: (input: PosPaymentInput) => void
  onPrepareCard?: () => void; initialMethod?: 'cash' | 'external_card'
  /** Set to offer paying by voucher. */
  restaurantId?: number; noTip?: boolean
}) {
  const { t, i18n } = useTranslation('posOrdering')
  const register = useCashRegister()
  const cashReady = !register || (!register.unavailable && register.status?.session?.state === 'open')
  // Once the Kasse is in use, a card payment outside a session would appear on no closing report.
  const closed = Boolean(register?.status?.enabled && !register.status.session)
  const [method, setMethod] = useState<'cash' | 'external_card'>(initialMethod)
  const [voucher, setVoucher] = useState<PosVoucher | null>(null)
  const [voucherOpen, setVoucherOpen] = useState(false)
  const voucherCents = voucher ? Math.min(voucher.balanceCents, amountDue) : 0
  // What cash or card still has to cover. A voucher payment records itself, so it needs no card preparation first.
  const totalCents = amountDue - voucherCents
  const onPrepareCard = voucher ? undefined : prepareCard
  const [tendered, setTendered] = useState('')
  const receivedId = useId()
  const [replaceAmount, setReplaceAmount] = useState(false)
  const [terminalConfirmed, setTerminalConfirmed] = useState(false)
  const [tip, setTip] = useState(0)
  const [tipTyping, setTipTyping] = useState(false)
  const [pad, setPad] = useState<'money' | 'keys'>(() => { try { return localStorage.getItem(PAD_KEY) === 'keys' ? 'keys' : 'money' } catch { return 'money' } })
  function switchPad(next: 'money' | 'keys') {
    setPad(next)
    try { localStorage.setItem(PAD_KEY, next) } catch { /* Remembering it is a convenience only. */ }
  }
  // How often each note or coin was tapped, so staff can compare it with the money in their hand.
  const [taps, setTaps] = useState<Partial<Record<number, number>>>({})
  const cents = method === 'cash' ? parseTenderedCents(tendered) : totalCents
  // A cash tip goes straight to the employee and stays out of the Kasse, so only card tips are recorded.
  const tipCents = method === 'external_card' ? tip : 0
  const valid = voucherCents > 0 && totalCents === 0 || (totalCents > 0 && cents !== null && cents >= totalCents && (method === 'cash' || terminalConfirmed))
  const money = (amount: number) => formatMoney(i18n.language, amount / 100, 'EUR')
  const setCash = (amount: number) => { setTendered(`${Math.floor(amount / 100)},${String(amount % 100).padStart(2, '0')}`); setReplaceAmount(true) }
  function press(key: string) {
    if (disabled || busy) return
    setTaps({})
    if (key === 'clear') { setTendered(''); setReplaceAmount(false); return }
    if (key === 'backspace') { setTendered(value => value.slice(0, -1)); setReplaceAmount(false); return }
    let next = replaceAmount ? '' : tendered
    if (key === ',') { if (next.includes(',')) return; next = (next || '0') + ',' }
    else {
      if (!/^[0-9]$/.test(key) || next.split(',')[1]?.length === 2) return
      next = next === '0' ? key : next + key
      if (next.split(',')[0].length > 7) return
    }
    setTendered(next); setReplaceAmount(false)
  }
  /** Tapping what the guest hands over adds it up, so nobody has to do the sum first. */
  function add(amount: number) {
    if (disabled || busy) return
    setCash((parseTenderedCents(tendered.replace(/,$/, '')) ?? 0) + amount)
    setTaps(current => ({ ...current, [amount]: (current[amount] ?? 0) + 1 }))
  }
  const cash = (value: number) => (
    <button key={value} type="button" disabled={disabled || busy} aria-label={denominationName(value)} onClick={() => add(value)}
      className="relative mx-auto block w-full max-w-[216px] touch-manipulation pos-wide:max-w-[148px] select-none rounded-md transition-transform duration-100 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none">
      <CashMark cents={value} />
      {taps[value] && <span className="absolute -end-1.5 -top-1.5 flex h-[30px] min-w-[30px] items-center justify-center rounded-[9px] bg-foreground px-1 text-[15px] font-bold text-background ring-2 ring-background">{taps[value]}</span>}
    </button>
  )
  const change = cents !== null && cents >= totalCents ? cents - totalCents : null
  const tipChoices = [...new Set([Math.ceil((totalCents + 1) / 100) * 100, Math.ceil((totalCents + 1) / 500) * 500])].map(value => value - totalCents)
  const tipPicker = !noTip && !onPrepareCard && totalCents > 0 && (
    <div className="space-y-1.5" role="group" aria-label={t('payment.tip')}>
      <span className="text-[15px] font-semibold text-muted-foreground">{t('payment.tip')}</span>
      <div className="flex flex-wrap gap-2">
        {tipChoices.map(value => <Button key={value} type="button" variant="ghost" aria-pressed={tipCents === value} disabled={disabled || busy}
          className={cn(QUIET, 'h-12 bg-card px-4', tipCents === value && PICKED)}
          onClick={() => { setTipTyping(false); setTip(tipCents === value ? 0 : value) }}>
          {t('payment.tipRoundUp', { amount: money(totalCents + value) })}
        </Button>)}
        <Button type="button" variant="ghost" aria-pressed={tipTyping} disabled={disabled || busy} className={cn(QUIET, 'h-12 bg-card px-4', tipTyping && PICKED)}
          onClick={() => { setTipTyping(!tipTyping); setTip(0) }}>{t('payment.tipAmount')}</Button>
      </div>
      {tipTyping && <input type="text" inputMode="decimal" autoFocus aria-label={t('payment.tipAmount')} placeholder="0,00" disabled={disabled || busy}
        className="h-12 w-32 rounded-xl bg-card px-3 text-end text-base font-semibold shadow-[0_0_0_1.5px_hsl(var(--primary))] focus-visible:outline-none"
        onChange={event => setTip(parseTenderedCents(event.target.value) ?? 0)} />}
    </div>
  )
  const shortcuts = [...new Set([totalCents, Math.ceil(totalCents / 500) * 500, Math.ceil(totalCents / 1000) * 1000, 2000, 5000, 10000])]
    .filter(value => value >= totalCents).sort((a, b) => a - b).slice(0, 4)
  const offerVoucher = restaurantId !== undefined && !voucher
  return (
    <div className="pos-print flex flex-1 flex-col gap-3.5 pos-wide:gap-2.5">
      <div className="flex items-baseline justify-between gap-3 px-1">
        <span className="text-xl font-semibold">{t('payment.amountDue')}</span>
        <strong className="text-[40px] font-bold leading-[48px] tracking-tight">{money(amountDue)}</strong>
      </div>
      {voucher && <div className="flex flex-col gap-1 px-1">
        <div className="flex items-center gap-2 text-base">
          <Gift aria-hidden className="h-5 w-5 shrink-0 text-pos-number" /><span className="min-w-0 truncate font-semibold">{t('voucher.applied', { code: voucher.code })}</span>
          <span className="ms-auto whitespace-nowrap">− {money(voucherCents)}</span>
          <Button type="button" variant="ghost" size="icon" className={cn(FLAT, 'h-11 w-11 shrink-0 rounded-xl hover:bg-secondary hover:text-foreground')} aria-label={t('voucher.remove')} disabled={disabled || busy} onClick={() => setVoucher(null)}><X /></Button>
        </div>
        <div className="flex items-baseline justify-between gap-3"><span className="text-muted-foreground">{t('voucher.rest')}</span><strong className="text-2xl font-bold">{money(totalCents)}</strong></div>
      </div>}
      {totalCents > 0 && <div role="radiogroup" aria-label={t('payment.method')} className={cn(SEGMENTS, 'grid', offerVoucher ? 'grid-cols-3' : 'grid-cols-2')}>
        {(['cash', 'external_card'] as const).map(value => {
          const on = method === value && !voucherOpen
          return <button key={value} type="button" role="radio" aria-checked={on} data-roving onKeyDown={event => rovingKeyDown(event, true)}
            tabIndex={on ? 0 : -1} disabled={disabled || busy} className={cn(SEGMENT, 'h-[60px] text-[17px] pos-wide:h-12', on && SEGMENT_ON)}
            onClick={() => { setMethod(value); setVoucherOpen(false); setTerminalConfirmed(false) }}>{t(`payment.${value}`)}</button>
        })}
        {offerVoucher && <button type="button" role="radio" aria-checked={voucherOpen} data-roving onKeyDown={event => rovingKeyDown(event, true)}
          tabIndex={voucherOpen ? 0 : -1} disabled={disabled || busy} className={cn(SEGMENT, 'h-[60px] text-[17px] pos-wide:h-12', voucherOpen && SEGMENT_ON)} onClick={() => setVoucherOpen(true)}>{t('payment.voucher')}</button>}
      </div>}
      {restaurantId !== undefined && !voucher && voucherOpen && <VoucherEntry restaurantId={restaurantId} dueCents={amountDue} onApply={value => { setVoucher(value); setVoucherOpen(false) }} />}
      {totalCents === 0 || voucherOpen ? null : method === 'cash' ? <>
        <CashRegisterNotice />
        <div className="flex flex-wrap gap-2">
          <div role="group" aria-labelledby={receivedId} tabIndex={0}
            className={cn(AMOUNT, 'min-w-0 flex-[1_1_200px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pos-wide:h-[60px]')}
            onKeyDown={event => {
              if (/^[0-9.,]$/.test(event.key) || ['Backspace', 'Delete'].includes(event.key)) {
                event.preventDefault(); press(event.key === 'Backspace' ? 'backspace' : event.key === 'Delete' ? 'clear' : event.key === '.' ? ',' : event.key)
              }
            }}>
            <span id={receivedId} className="font-medium text-muted-foreground">{t('payment.cashReceived')}</span>
            <span className="text-[28px] font-bold"><output aria-live="polite">{tendered || '0,00'}</output> €</span>
          </div>
          <div className="flex flex-auto gap-2 [&>button]:flex-1">
            {shortcuts.slice(0, pad === 'money' ? 1 : 2).map((amount, index) => (
              <Button key={amount} type="button" variant="ghost" disabled={disabled || busy} className={SIDE}
                onClick={() => { setTaps({}); setCash(amount) }}>{index === 0 ? t('payment.exact') : money(amount)}</Button>
            ))}
            {pad === 'money' && <Button type="button" variant="ghost" disabled={disabled || busy} className={SIDE} onClick={() => press('clear')}>{t('keypad.clear')}</Button>}
            <Button type="button" variant="ghost" disabled={disabled || busy} className={SIDE} onClick={() => switchPad(pad === 'money' ? 'keys' : 'money')}>
              {t(pad === 'money' ? 'payment.padKeys' : 'payment.padMoney')}</Button>
          </div>
        </div>
        {pad === 'money' ? (
          <div role="group" aria-label={t('payment.cashReceived')} className="flex flex-col gap-2">
            <div className="grid grid-cols-3 gap-2">{CASH_NOTES.map(cash)}</div>
            <div className="grid grid-cols-6 gap-2 [&>button]:max-w-[96px] pos-wide:[&>button]:max-w-[76px]">{CASH_COINS.map(cash)}</div>
          </div>
        ) : (
          <div role="group" aria-label={t('payment.numberPad')} className="grid grid-cols-3 gap-2">
            {['7', '8', '9', '4', '5', '6', '1', '2', '3', ',', '0', 'backspace'].map(key => (
              <Button key={key} type="button" variant="ghost" disabled={disabled || busy} className={cn(QUIET, 'h-16 bg-card text-2xl shadow-[0_0_0_1px_var(--pos-hair)] hover:bg-card/70 [&_svg]:size-6')}
                aria-label={key === 'backspace' ? t('payment.deleteDigit') : key === ',' ? t('payment.decimal') : undefined}
                onClick={() => press(key)}>{key === 'backspace' ? <Delete aria-hidden /> : key}</Button>
            ))}
          </div>
        )}
      </> : closed ? <CashRegisterNotice /> : onPrepareCard ? <p className="px-1 text-[15px] text-muted-foreground">{t('payment.prepareCardHint')}</p> : (
        <div className="flex flex-col gap-3">
          {tipPicker}
          <p className="px-1 text-[15px] text-muted-foreground">{t('payment.terminalInstructions', { amount: money(totalCents + tipCents) })}</p>
          <label className="flex min-h-[60px] cursor-pointer items-center gap-3 rounded-xl bg-card px-4 py-3 text-base font-semibold shadow-pos">
            <input type="checkbox" checked={terminalConfirmed} disabled={disabled || busy} className="pos-check"
              onChange={event => setTerminalConfirmed(event.target.checked)} />
            {t('payment.terminalConfirmed')}
          </label>
        </div>
      )}
      {!voucherOpen && <div data-pay-foot className="mt-auto flex flex-col gap-2.5">
        {method === 'cash' && totalCents > 0 && <div className="flex items-baseline justify-between gap-3 border-t px-1 pt-3 pos-wide:pt-1.5" aria-live="polite">
          <span className="text-xl font-semibold">{t('payment.change')}</span>
          <strong className="text-[34px] font-bold leading-10">{change === null ? '–' : money(change)}</strong>
        </div>}
        <p className="px-1 text-xs text-muted-foreground">{t('payment.pilotNotice')}</p>
        <Button type="button" className={cn(MAIN, 'h-[76px] w-full text-[21px] pos-wide:h-16 [&_svg]:size-6')} disabled={disabled || busy || closed || (totalCents > 0 && method === 'cash' && !cashReady) || (method === 'external_card' && onPrepareCard ? false : !valid)} onClick={() => {
          if (totalCents === 0) { if (valid) onConfirm({ idempotencyKey: crypto.randomUUID(), method: 'voucher', expectedTotalCents: amountDue, tenderedCents: 0, terminalConfirmed: false, tipCents: 0, voucherCode: voucher?.code ?? '', voucherCents }); return }
          if (method === 'external_card' && onPrepareCard) { onPrepareCard(); return }
          if (valid && (method !== 'cash' || cashReady)) onConfirm({ idempotencyKey: crypto.randomUUID(), method, expectedTotalCents: amountDue,
            tenderedCents: method === 'cash' ? cents ?? 0 : totalCents + tipCents, terminalConfirmed: method === 'external_card', tipCents, voucherCode: voucher?.code ?? '', voucherCents })
        }}>
          {busy && <Loader2 aria-hidden className="animate-spin" />}
          {t(totalCents === 0 ? 'voucher.redeem' : method === 'cash' ? 'payment.confirmCash' : onPrepareCard ? 'payment.prepareCard' : 'payment.confirmCard')}
        </Button>
      </div>}
    </div>
  )
}
